import { useEffect, useState } from "react";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { Money } from "@/components/Money";
import { Card } from "@/components/dashboard/ErpWidgets";
import { FilterBar } from "@/components/filters/FilterBar";
import { TileBadge } from "@/components/filters/TileBadge";
import api, { apiError } from "@/lib/api";
import { useFilters } from "@/lib/filters";
import { monthLabel } from "@/lib/format";

const ALL = ["2000-01-01", new Date().toISOString().slice(0, 10)];
const HEADS = {
  accounts: [["month", "Month"], ["account", "Account"], ["description", "Description"], ["amount", "Amount"]],
  monthly: [["month", "Month"], ["lines", "Sales lines"], ["amount", "Amount"]],
};

const RowsTable = ({ view }) => {
  const cols = HEADS[view.rows_kind];
  if (!cols) return null;
  return (
    <Card title="Records" subtitle="From the uploads · change account categories on Expense mapping" testid="line-rows-card">
      <div className="max-h-[420px] overflow-auto">
        <table className="w-full text-sm" data-testid="line-rows-table">
          <thead className="sticky top-0 bg-white text-xs uppercase tracking-wide text-slate-500">
            <tr>{cols.map(([k, l]) => <th key={k} className={`py-2 pr-4 ${["amount", "lines"].includes(k) ? "text-right" : "text-left"}`}>{l}</th>)}</tr>
          </thead>
          <tbody>
            {view.rows.length === 0 && <tr><td colSpan={cols.length} className="py-6 text-center text-slate-500" data-testid="line-rows-empty">No records in this period</td></tr>}
            {view.rows.map((r, i) => (
              <tr key={i} className="border-t border-slate-100" data-testid="line-row">
                {cols.map(([k]) => <td key={k} className={`py-2 pr-4 ${["amount", "lines"].includes(k) ? "text-right" : ""}`}>{k === "amount" ? <Money value={r[k]} /> : k === "month" ? monthLabel(r[k]) : r[k] ?? "—"}</td>)}
              </tr>
            ))}
          </tbody>
          <tfoot><tr className="border-t-2 border-slate-900 font-semibold" data-testid="line-rows-total"><td colSpan={cols.length - 1} className="py-2">Total ({view.rows.length})</td><td className="text-right"><Money value={view.rows_total} /></td></tr></tfoot>
        </table>
      </div>
    </Card>
  );
};

const SectionLines = ({ section }) => section && (
  <Card title={section.label} subtitle="Same figures as the Dashboard and Reports for this period" testid="section-lines-card">
    <table className="w-full text-sm" data-testid="section-lines-table">
      <tbody>
        {section.lines.map((l) => <tr key={l.key} className="border-t border-slate-100" data-testid={`section-line-${l.key}`}><td className="py-2">{l.label}</td><td className="text-right"><Money value={l.total} /></td></tr>)}
        <tr className="border-t-2 border-slate-900 font-semibold" data-testid="section-total-row"><td className="py-2">Section total <TileBadge status={section.status} testid="section-total-badge" /></td><td className="text-right"><Money value={section.total} /></td></tr>
      </tbody>
    </table>
  </Card>
);

export default function LinePage({ lineKey }) {
  const fl = useFilters(null);
  const [view, setView] = useState(null);
  useEffect(() => { api.get(`/sections/category/${lineKey}?${fl.key}`).then((r) => setView(r.data)).catch((e) => toast.error(apiError(e))); }, [lineKey, fl.key]);
  return (
    <div className="space-y-6" data-testid={`line-page-${lineKey}`}>
      <PageHeader brand eyebrow={view?.section?.label} title={view?.label || "…"} subtitle="From the uploads · read-only" />
      <FilterBar fl={fl} periodLabel="All history" allHistory={ALL} />
      {view && (
        <>
          <div className="rise-in max-w-sm rounded-lg border border-slate-900 bg-slate-900 p-4 text-white shadow-sm" data-testid="line-total">
            <div className="text-xs text-slate-300">{view.label}</div><Money value={view.total} className="mt-1 block text-xl font-semibold" data-testid="line-total-value" />
            <div className="mt-2"><TileBadge status={view.status} testid="line-total-badge" /></div>
          </div>
          <RowsTable view={view} />
          <SectionLines section={view.section} />
        </>
      )}
    </div>
  );
}
